import React, { FC, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import { mapCustomFieldsToForm } from "go-app/components/CustomFields";
import CustomFieldsForm from "go-app/components/CustomFields/CustomFieldsForm";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { FormInputWithButton } from "go-form/components/FormInputWithButton";
import FormNumberInput from "go-form/components/FormNumberInput";
import { useCustomErrors } from "go-form/hooks";
import { CustomValidationConfig, CustomValidationConstraint } from "go-form/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { ContactApi } from "../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../services/Api/api";

interface Props {
	contact?: ContactApi;
	customFieldsConfig?: CustomFieldTemplateApi[];
	backPath?: string;
}

const ContactForm: FC<Props> = ({ contact, customFieldsConfig, backPath }) => {
	const form = useForm<ContactApi>({
		criteriaMode: "all",
		defaultValues: {
			...contact,
			custom_fields: mapCustomFieldsToForm(customFieldsConfig || [], contact?.custom_fields || []),
		},
	});
	const {
		control,
		register,
		formState,
		formState: { errors },
		setError,
		getValues,
		reset,
		clearErrors,
		handleSubmit,
		setValue,
		watch,
	} = form;
	const { addFlash, addSuccessFlash } = useFlash();
	const [loading, setLoading] = useState<boolean>(false);
	const [searchingClient, setSearchingClient] = useState<boolean>(false);
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const history = useHistory();
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);

	const onSubmit = handleSubmit(async (data: ContactApi) => {
		if (!validateCustomErrors()) {
			return;
		}

		setLoading(true);
		try {
			let res;
			if (data?.id) {
				res = await api.organization().updateContact(data, { include: "custom_fields" });

				reset({
					...res,
					contact_details: {
						...res.contact_details,
						phone_number: res.contact_details?.phone_number || "",
					},
					custom_fields: mapCustomFieldsToForm(customFieldsConfig || [], res.custom_fields),
				});
			} else {
				res = await api.organization().createContact(data);
				history.push(`/${organization.id}/contacts/${res.id}`);
			}
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const onSearchClient = async () => {
		const taxIdNo = getValues("tax_id_no");
		setSearchingClient(true);
		try {
			const res = await api.organization().getDataByTaxIdNo(taxIdNo);
			setValue("address.city", res.address.city);
			setValue("address.build_nr", res.address.build_nr);
			setValue("name", res.name);
			setValue("address.country", res.address.country);
			setValue("address.street", res.address.street);
			setValue("address.zip_code", res.address.zip_code);
			setValue("address.flat_nr", res.address.flat_nr);
			clearErrors();
		} catch (err) {
			handleError.form(err, setError);
		}
		setSearchingClient(false);
	};

	const nameValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
			utils: { watch },
		}),
		[setErrors, watch]
	) satisfies CustomValidationConfig;

	return (
		<FormDirty formState={formState} key="contact-form" onSubmit={onSubmit} loading={loading} backPath={backPath}>
			<fieldset className="form-group">
				<div className="row">
					<div className="col-md-6">
						<FormInput
							data-testid="contact-form-name-input"
							label={`${t("common.word.name", { ns: "lib" })}`}
							register={register}
							name="name"
							errors={errors}
							customValidationConfig={nameValidationConfig}
						/>
					</div>
					<div className="col-md-6">
						<FormInputWithButton
							data-testid="contact-form-tax-id-no-input"
							buttonText={t("common.word.tax_id_no.search", { ns: "lib" })}
							buttonLoading={searchingClient}
							onButtonClick={onSearchClient}
							label={t("common.word.tax_id_no.title", { ns: "lib" })}
							register={register}
							name="tax_id_no"
							errors={errors}
						/>
					</div>
				</div>
			</fieldset>
			<fieldset className="form-group no-gutters">
				<h5>{t("common.word.address", { ns: "lib" })}</h5>
				<div className="row">
					<div className="col-md-3">
						<FormInput
							data-testid="contact-form-address-country-input"
							label={t("common.address.country", { ns: "lib" })}
							register={register}
							name="address.country"
							errors={errors}
						/>
					</div>
					<div className="col-md-3">
						<FormInput
							data-testid="contact-form-address-zip-code-input"
							label={t("common.address.zip_code", { ns: "lib" })}
							register={register}
							name="address.zip_code"
							errors={errors}
						/>
					</div>
					<div className="col-md-6">
						<FormInput
							data-testid="contact-form-address-city-input"
							label={t("common.address.city", { ns: "lib" })}
							register={register}
							name="address.city"
							errors={errors}
						/>
					</div>
					<div className="col-md-6">
						<FormInput
							data-testid="contact-form-address-street-input"
							label={t("common.address.street", { ns: "lib" })}
							register={register}
							name="address.street"
							errors={errors}
						/>
					</div>
					<div className="col-md-3">
						<FormInput
							data-testid="contact-form-address-build-nr-input"
							label={t("common.address.build_nr", { ns: "lib" })}
							register={register}
							name="address.build_nr"
							errors={errors}
						/>
					</div>
					<div className="col-md-3">
						<FormInput
							data-testid="contact-form-address-flat-nr-input"
							label={t("common.address.flat_nr", { ns: "lib" })}
							register={register}
							name="address.flat_nr"
							errors={errors}
						/>
					</div>
				</div>
			</fieldset>
			<fieldset className="form-group no-gutters">
				<h5>{t("modules.contact.header.title")}</h5>
				<div className="row">
					<div className="col-md-6">
						<FormInput
							data-testid="contact-form-email-input"
							label={t("common.word.email", { ns: "lib" })}
							register={register}
							name="contact_details.email"
							errors={errors}
						/>
					</div>
					<div className="col-md-6">
						<FormNumberInput
							data-testid="contact-form-phone-number-input"
							label={t("common.word.phone_number", { ns: "lib" })}
							control={control}
							name="contact_details.phone_number"
							suffix=""
							errors={errors}
						/>
					</div>
				</div>
			</fieldset>
			{customFieldsConfig && customFieldsConfig.length > 0 && (
				<fieldset data-testid="contact-form-advanced-section">
					<h5>{t("common.word.advanced", { ns: "lib" })}</h5>
					<CustomFieldsForm
						customFieldsConfig={customFieldsConfig}
						customFields={contact?.custom_fields || []}
						form={form}
						setErrors={setErrors}
					/>
				</fieldset>
			)}
		</FormDirty>
	);
};

export default ContactForm;
